import { useEffect, useState } from "react";
import { ErrorBoundary, Suspense } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SubscriptionFiltersComponent } from "./subscription-filters";
import { SubscriptionTable } from "./subscription-table";
import { subscriptionQueryOptions } from "@/hooks/use-subscriptions";
import { usePagination } from "@/hooks/use-pagination";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import type { AdminSubscriptionFilters } from "@/types/subscription";

export function SubscriptionListTab() {
  const [filters, setFiltersState] = useState<AdminSubscriptionFilters>({});
  const pagination = usePagination({ initialLimit: 10 });
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery(
      subscriptionQueryOptions(filters, {
        page: pagination.page + 1,
        limit: pagination.limit,
      })
    );
  }, [queryClient, filters, pagination.page, pagination.limit]);

  const setFilters = (newFilters: AdminSubscriptionFilters) => {
    setFiltersState(newFilters);
    pagination.setPage(1);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>구독 목록</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-4 md:px-6">
        <SubscriptionFiltersComponent
          filters={filters}
          onFiltersChange={setFilters}
        />

        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
              <Suspense fallback={<TableLoadingFallback />}>
                <SuspenseQuery
                  {...subscriptionQueryOptions(filters, {
                    page: pagination.page,
                    limit: pagination.limit,
                  })}
                >
                  {({ data }) => (
                    <SubscriptionTable
                      data={data.items}
                      meta={data.meta}
                      pagination={pagination}
                    />
                  )}
                </SuspenseQuery>
              </Suspense>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </CardContent>
    </Card>
  );
}
